// BatchPromissoryNoteModal.tsx - Seri Senet Kesme Modalı
// Her senedin tutarı ayrı ayrı düzenlenebilir + Senet detay bilgileri
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Divider,
  InputAdornment,
  CircularProgress,
  Autocomplete,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionIcon from '@mui/icons-material/Description';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  CheckNoteDirection,
  CheckNoteDirectionValue,
  CheckNoteType,
  CheckNoteTypeValue,
  CreateBatchPromissoryNotesDto,
  createBatchPromissoryNotes,
  formatCurrency,
  previewNextDocumentNumber
} from '../../../services/checkNoteService';
import CurrencyService from '../../../services/CurrencyService';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import WarningIcon from '@mui/icons-material/Warning';
import { AppDatePicker } from '../../common/AppDatePicker';
import GuideButton from '../../common/guide/GuideButton';

interface BatchPromissoryNoteModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  companyId: string;
}

interface Account {
  id: number;
  name: string;
  code: string;
}

// Vade aralığı birimi
type IntervalUnit = 'days' | 'months';

const BatchPromissoryNoteModal: React.FC<BatchPromissoryNoteModalProps> = ({
  open,
  onClose,
  onSave,
  companyId
}) => {
  const { t, i18n } = useTranslation('common2');
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';

  // Form state
  const [direction, setDirection] = useState<CheckNoteDirectionValue>(CheckNoteDirection.RECEIVED as CheckNoteDirectionValue);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [drawerName, setDrawerName] = useState('');
  const [count, setCount] = useState<number>(3);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [currency, setCurrency] = useState('TRY');
  const [exchangeRate, setExchangeRate] = useState<number>(1);
  const [firstDueDate, setFirstDueDate] = useState<string>('');
  
  // Vade aralığı state'leri
  const [intervalValue, setIntervalValue] = useState<number>(1);
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>('months');
  
  const [description, setDescription] = useState('');

  // Belge numarası sunucuda üretilir; önizleme için sıradaki numara API'den alınır
  const [nextDocumentNumber, setNextDocumentNumber] = useState<string | null>(null);

  // Senet detay bilgileri
  const [debtorIdentityNo, setDebtorIdentityNo] = useState('');
  const [debtorAddress, setDebtorAddress] = useState('');
  const [guarantorName, setGuarantorName] = useState('');
  const [guarantorIdentityNo, setGuarantorIdentityNo] = useState('');
  const [guarantorAddress, setGuarantorAddress] = useState('');
  const [jurisdictionPlace, setJurisdictionPlace] = useState('');
  const [issuePlace, setIssuePlace] = useState('');

  // Her senedin özel tutarı
  const [customAmounts, setCustomAmounts] = useState<number[]>([]);

  // Data loading states
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Gün cinsinden vade aralığı hesapla
  const dueDateIntervalDays = useMemo(() => {
    if (intervalUnit === 'days') {
      return intervalValue;
    }
    // Ay için yaklaşık 30 gün
    return intervalValue * 30;
  }, [intervalValue, intervalUnit]);

  // Default tarih: bugün + 1 ay
  useEffect(() => {
    if (open) {
      const today = new Date();
      today.setMonth(today.getMonth() + 1);
      setFirstDueDate(today.toISOString().split('T')[0]);
    }
  }, [open]);

  // Load accounts
  useEffect(() => {
    if (open && companyId) {
      loadAccounts();
    }
  }, [open, companyId]);

  // Sıradaki belge numarasını önizleme için çek
  useEffect(() => {
    if (!open) {
      setNextDocumentNumber(null);
      return;
    }

    let cancelled = false;
    previewNextDocumentNumber(CheckNoteType.PROMISSORY_NOTE as CheckNoteTypeValue)
      .then(number => {
        if (!cancelled) setNextDocumentNumber(number);
      })
      .catch(err => {
        console.warn('[BatchPromissoryNote] Sıradaki belge numarası alınamadı:', err);
      });

    return () => { cancelled = true; };
  }, [open]);

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
      // API yanıtı { success, data } veya direkt array olabilir 
      const accountsData = response.data?.data || response.data?.Data || response.data;
      if (accountsData && Array.isArray(accountsData)) {
        setAccounts(accountsData.map((acc: any) => ({
          id: acc.Id || acc.id,
          name: acc.CompanyName || acc.companyName || acc.Name || acc.name,
          code: acc.AccountCode || acc.accountCode || acc.Code || acc.code || ''
        })));
      }
    } catch (err) {
      console.error('Hesaplar yüklenirken hata:', err);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Exchange rate for non-TRY currencies
  useEffect(() => {
    const fetchExchangeRate = async () => {
      if (currency !== 'TRY') {
        try {
          const rates = await CurrencyService.fetchExchangeRates();
          const currencyRate = rates.find(r => r.code === currency);
          if (currencyRate) {
            setExchangeRate(currencyRate.buying);
          }
        } catch {
          setExchangeRate(1);
        }
      } else {
        setExchangeRate(1);
      }
    };
    fetchExchangeRate();
  }, [currency]);

  // Eşit dağıt fonksiyonu (küsüratı son senete ekle)
  const distributeEvenly = useCallback(() => {
    if (count <= 0 || totalAmount <= 0) {
      setCustomAmounts(new Array(count).fill(0));
      return;
    }

    const baseAmount = Math.floor((totalAmount / count) * 100) / 100; // 2 ondalık yuvarla (aşağı)
    const remainder = Math.round((totalAmount - (baseAmount * count)) * 100) / 100; // Küsürat

    const amounts = new Array(count).fill(baseAmount);
    // Küsüratı son senete ekle
    amounts[count - 1] = Math.round((amounts[count - 1] + remainder) * 100) / 100;

    setCustomAmounts(amounts);
  }, [count, totalAmount]);

  // Toplam tutar veya senet adedi değiştiğinde otomatik dağıt
  useEffect(() => {
    distributeEvenly();
  }, [distributeEvenly]);

  // Tek bir senetin tutarını değiştir
  const handleAmountChange = (index: number, newAmount: number) => {
    const newAmounts = [...customAmounts];
    newAmounts[index] = newAmount;
    setCustomAmounts(newAmounts);
  };

  // Farkı satira ekle veya çıkar
  const applyDifferenceToRow = (index: number) => {
    if (Math.abs(amountDifference) < 0.01) return;
    
    const newAmounts = [...customAmounts];
    newAmounts[index] = Math.round((newAmounts[index] + amountDifference) * 100) / 100;
    setCustomAmounts(newAmounts);
  };

  // Toplam tutarı customAmounts'tan hesapla
  const calculatedTotal = useMemo(() => {
    return customAmounts.reduce((sum, amount) => sum + (amount || 0), 0);
  }, [customAmounts]);

  // Fark göster
  const amountDifference = useMemo(() => {
    return Math.round((totalAmount - calculatedTotal) * 100) / 100;
  }, [totalAmount, calculatedTotal]);

  // Vade tarihleri hesapla
  const dueDates = useMemo(() => {
    if (!firstDueDate || count <= 0) return [];
    
    const dates: Date[] = [];
    const baseDate = new Date(firstDueDate);
    
    for (let i = 0; i < count; i++) {
      const dueDate = new Date(baseDate);
      if (intervalUnit === 'months') {
        dueDate.setMonth(dueDate.getMonth() + (i * intervalValue));
      } else {
        dueDate.setDate(dueDate.getDate() + (i * intervalValue));
      }
      dates.push(dueDate);
    }
    
    return dates;
  }, [firstDueDate, count, intervalValue, intervalUnit]);

  // Belge numaraları: sunucudan gelen sıradaki numara (SN-YYYY-00042) baz alınıp
  // ardışık olarak artırılır. Numaralar kayıt anında sunucuda üretildiği için
  // arada başka kayıt açılırsa gerçek numaralar bu önizlemeden farklı olabilir.
  const documentNumbers = useMemo(() => {
    if (!nextDocumentNumber) {
      return Array.from({ length: count }, () => '…');
    }

    const match = nextDocumentNumber.match(/^(.*?)(\d+)$/);
    if (!match) {
      return Array.from({ length: count }, () => nextDocumentNumber);
    }

    const [, prefix, sequence] = match;
    const startSeq = parseInt(sequence, 10);

    return Array.from(
      { length: count },
      (_, i) => `${prefix}${String(startSeq + i).padStart(sequence.length, '0')}`
    );
  }, [count, nextDocumentNumber]);

  // Seçilen hesap
  const selectedAccount = accounts.find(a => a.id === selectedAccountId);

  // Validasyon - toplam tutar eşleşmiyorsa kaydet butonu disable
  const isValid = useMemo(() => {
    return (
      selectedAccountId !== null &&
      count > 0 &&
      count <= 100 &&
      totalAmount > 0 &&
      customAmounts.length === count &&
      customAmounts.every(a => a > 0) &&
      Math.abs(amountDifference) < 0.01 && // Fark 1 kuruştan az olmalı
      firstDueDate &&
      intervalValue > 0
    );
  }, [selectedAccountId, count, totalAmount, customAmounts, amountDifference, firstDueDate, intervalValue]);

  const handleSave = async () => {
    if (!isValid) return;

    setLoading(true);
    setError(null);

    try {
      const dto: CreateBatchPromissoryNotesDto = {
        companyId,
        direction,
        accountId: selectedAccountId!,
        accountName: selectedAccount?.name,
        drawerName: drawerName || undefined,
        
        // Senet detay bilgileri
        debtorIdentityNo: debtorIdentityNo || undefined,
        debtorAddress: debtorAddress || undefined,
        guarantorName: guarantorName || undefined,
        guarantorIdentityNo: guarantorIdentityNo || undefined,
        guarantorAddress: guarantorAddress || undefined,
        jurisdictionPlace: jurisdictionPlace || undefined,
        issuePlace: issuePlace || undefined,
        
        count,
        totalAmount,
        customAmounts,
        currency,
        exchangeRate: currency !== 'TRY' ? exchangeRate : undefined,
        firstDueDate,
        dueDateIntervalDays,
        description: description || undefined
      };

      await createBatchPromissoryNotes(dto);
      onSave();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('checksNotes.errors.batchCreateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDirection(CheckNoteDirection.RECEIVED as CheckNoteDirectionValue);
    setSelectedAccountId(null);
    setDrawerName('');
    setCount(3);
    setTotalAmount(0);
    setCustomAmounts([]);
    setCurrency('TRY');
    setExchangeRate(1);
    setFirstDueDate('');
    setIntervalValue(1);
    setIntervalUnit('months');
    setNextDocumentNumber(null);
    setDescription('');
    
    // Senet bilgileri reset
    setDebtorIdentityNo('');
    setDebtorAddress('');
    setGuarantorName('');
    setGuarantorIdentityNo('');
    setGuarantorAddress('');
    setJurisdictionPlace('');
    setIssuePlace('');
    
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: '80vh', borderRadius: 2 } }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        bgcolor: 'secondary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon />
          <Typography variant="h6">{t('checksNotes.batch.title')}</Typography>
          <GuideButton guideKey="finance.batchPromissoryNote" color="inherit" size="small" />
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Sol Panel - Form */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={2}>
              {/* Yön Seçimi */}
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('checksNotes.form.direction')}</InputLabel>
                  <Select
                    value={direction}
                    onChange={(e) => setDirection(e.target.value as CheckNoteDirectionValue)}
                    label={t('checksNotes.form.direction')}
                  >
                    <MenuItem value={CheckNoteDirection.RECEIVED}>{t('checksNotes.batch.directionReceivedLabel')}</MenuItem>
                    <MenuItem value={CheckNoteDirection.GIVEN}>{t('checksNotes.batch.directionGivenLabel')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Cari Hesap Seçimi */}
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  options={accounts}
                  getOptionLabel={(option) => option.name}
                  value={selectedAccount || null}
                  onChange={(_, newValue) => {
                    setSelectedAccountId(newValue?.id || null);
                    if (newValue) {
                      setDrawerName(newValue.name);
                    }
                  }}
                  loading={loadingAccounts}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('checksNotes.form.account')}
                      placeholder={t('checksNotes.form.accountPlaceholder')}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingAccounts && <CircularProgress size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Borçlu Adı */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('checksNotes.form.drawerName')}
                  value={drawerName}
                  onChange={(e) => setDrawerName(e.target.value)}
                />
              </Grid>

              {/* Borçlu TC */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('checksNotes.form.debtorIdentityNo')}
                  value={debtorIdentityNo}
                  onChange={(e) => setDebtorIdentityNo(e.target.value)}
                  inputProps={{ maxLength: 11 }}
                />
              </Grid>
              {/* Düzenleme Yeri */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('checksNotes.form.issuePlace')}
                  value={issuePlace}
                  onChange={(e) => setIssuePlace(e.target.value)}
                />
              </Grid>
              {/* Borçlu Adresi */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('checksNotes.form.debtorAddress')}
                  value={debtorAddress}
                  onChange={(e) => setDebtorAddress(e.target.value)}
                />
              </Grid>
              {/* Kefil Adı */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('checksNotes.form.guarantorName')}
                  value={guarantorName}
                  onChange={(e) => setGuarantorName(e.target.value)}
                />
              </Grid>
              {/* Kefil TC */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('checksNotes.form.guarantorIdentityNo')}
                  value={guarantorIdentityNo}
                  onChange={(e) => setGuarantorIdentityNo(e.target.value)}
                  inputProps={{ maxLength: 11 }}
                />
              </Grid>
              {/* Kefil Adresi */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('checksNotes.form.guarantorAddress')}
                  value={guarantorAddress}
                  onChange={(e) => setGuarantorAddress(e.target.value)}
                />
              </Grid>
              {/* Yetkili Mahkeme */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('checksNotes.form.jurisdictionPlace')}
                  value={jurisdictionPlace}
                  onChange={(e) => setJurisdictionPlace(e.target.value)}
                  helperText={t('checksNotes.form.jurisdictionPlaceHelp')}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="caption" color="text.secondary">{t('checksNotes.batch.countAndAmount')}</Typography>
                </Divider>
              </Grid>

              {/* Senet Sayısı */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('checksNotes.batch.count')}
                  type="number"
                  value={count}
                  onChange={(e) => setCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                  inputProps={{ min: 1, max: 100 }}
                />
              </Grid>

              {/* Para Birimi */}
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('checksNotes.form.currency')}</InputLabel>
                  <Select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    label={t('checksNotes.form.currency')}
                  >
                    <MenuItem value="TRY">TRY</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="GBP">GBP</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Toplam Tutar */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('checksNotes.batch.totalAmount')}
                  type="number"
                  value={totalAmount || ''}
                  onChange={(e) => setTotalAmount(parseFloat(e.target.value) || 0)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{currency}</InputAdornment>
                  }}
                  helperText={t('checksNotes.batch.totalAmountHelp')}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="caption" color="text.secondary">{t('checksNotes.batch.dueInfo')}</Typography>
                </Divider>
              </Grid>

              {/* İlk Vade Tarihi */}
              <Grid item xs={12}>
                <AppDatePicker
                  fullWidth
                  size="small"
                  label={t('checksNotes.batch.firstDueDate')}
                  value={firstDueDate}
                  onChange={setFirstDueDate}
                />
              </Grid>

              {/* Vade Aralığı - Değer ve Birim */}
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('checksNotes.batch.interval')}
                  type="number"
                  value={intervalValue}
                  onChange={(e) => setIntervalValue(Math.max(1, parseInt(e.target.value) || 1))}
                  inputProps={{ min: 1, max: 365 }}
                />
              </Grid>
              <Grid item xs={7}>
                <ToggleButtonGroup
                  value={intervalUnit}
                  exclusive
                  onChange={(_, newUnit) => newUnit && setIntervalUnit(newUnit)}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="days">{t('checksNotes.batch.days')}</ToggleButton>
                  <ToggleButton value="months">{t('checksNotes.batch.months')}</ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              {/* Açıklama */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('checksNotes.form.notes')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Sağ Panel - Önizleme Tablosu */}
          <Grid item xs={12} md={7}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {t('checksNotes.batch.editableAmounts')}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {t('checksNotes.batch.total')}: {formatCurrency(calculatedTotal, currency)}
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={50}>#</TableCell>
                    <TableCell width={120}>{t('checksNotes.table.documentNumber')}</TableCell>
                    <TableCell width={120}>{t('checksNotes.table.dueDate')}</TableCell>
                    <TableCell>{t('checksNotes.table.amount')}</TableCell>
                    <TableCell width={80} align="center">{t('checksNotes.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customAmounts.map((amount, index) => (
                    <TableRow key={index} sx={index === 0 ? { bgcolor: 'action.hover' } : {}}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{documentNumbers[index]}</TableCell>
                      <TableCell>
                        {dueDates[index]?.toLocaleDateString(locale) || '-'}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={amount || ''}
                          onChange={(e) => {
                            const newValue = parseFloat(e.target.value) || 0;
                            handleAmountChange(index, newValue);
                          }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">{currency}</InputAdornment>
                          }}
                          sx={{ 
                            width: '100%',
                            '& .MuiInputBase-input': { 
                              textAlign: 'right',
                              fontWeight: index === 0 ? 'bold' : 'normal'
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {Math.abs(amountDifference) >= 0.01 && (
                          <Tooltip title={t('checksNotes.batch.applyDifferenceTooltip', {
                            action: amountDifference > 0 ? t('checksNotes.batch.add') : t('checksNotes.batch.subtract'),
                            amount: `${amountDifference > 0 ? '+' : ''}${amountDifference.toFixed(2)}`,
                            currency
                          })}>
                            <Chip
                              size="small"
                              icon={amountDifference > 0 ? <AddCircleOutlineIcon /> : <RemoveCircleOutlineIcon />}
                              label={`${amountDifference > 0 ? '+' : ''}${amountDifference.toFixed(2)}`}
                              color={amountDifference > 0 ? 'success' : 'error'}
                              variant="outlined"
                              onClick={() => applyDifferenceToRow(index)}
                              sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Özet */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>{t('checksNotes.batch.countLabel')}:</strong> {count}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>{t('checksNotes.batch.totalAmountLabel')}:</strong> {formatCurrency(totalAmount, currency)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>{t('checksNotes.batch.firstDueLabel')}:</strong> {dueDates[0]?.toLocaleDateString(locale) || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>{t('checksNotes.batch.lastDueLabel')}:</strong> {dueDates[count - 1]?.toLocaleDateString(locale) || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>{t('checksNotes.batch.intervalLabel')}:</strong> {intervalValue} {intervalUnit === 'months' ? t('checksNotes.batch.months') : t('checksNotes.batch.days')}
                  </Typography>
                </Grid>
                {/* Tutar Uyuşmazlığı Uyarısı */}
                {Math.abs(amountDifference) >= 0.01 && (
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      p: 1.5, 
                      bgcolor: 'warning.light', 
                      borderRadius: 1,
                      mt: 1
                    }}>
                      <WarningIcon color="warning" fontSize="small" />
                      <Typography variant="body2" fontWeight="bold" color="warning.dark">
                        {t('checksNotes.batch.amountDifference')}: {amountDifference > 0 ? '+' : ''}{formatCurrency(amountDifference, currency)}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('checksNotes.form.cancel')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSave}
          disabled={loading || !isValid}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
        >
          {loading ? t('checksNotes.batch.creating') : t('checksNotes.batch.createCount', { count })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchPromissoryNoteModal;
